import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Droplets, Calendar, Plus, ChevronRight } from 'lucide-react';
import { cn } from '../ui/utils'; // Assuming this handles class merging

interface WellEntry {
    id?: string;
    timestamp: string;
    oilProduction: number; // or oilProduction
    waterProduction: number;
    staticPressure?: number;
    dropPressure?: number;
    notes?: string;
}

interface RecentEntriesCardProps {
    entries: WellEntry[];
    onAddEntry: () => void;
    onViewAll?: () => void;
    isDarkMode: boolean;
}

export function RecentEntriesCard({ entries, onAddEntry, onViewAll, isDarkMode }: RecentEntriesCardProps) {
    if (entries.length === 0) {
        return (
            <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
                <CardContent className="p-8 flex flex-col items-center text-center">
                    <div className={cn("p-4 rounded-full mb-4", isDarkMode ? "bg-slate-800 text-slate-500" : "bg-slate-100 text-slate-400")}>
                        <Calendar className="w-8 h-8" />
                    </div>
                    <h3 className={cn("text-base font-semibold mb-1", isDarkMode ? "text-white" : "text-slate-900")}>No Entries Yet</h3>
                    <p className={cn("text-sm mb-4 max-w-[200px]", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                        Start tracking production by adding your first entry.
                    </p>
                    <Button onClick={onAddEntry} className="bg-blue-600 hover:bg-blue-700 text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Entry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={cn(isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200")}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className={cn("text-base font-semibold", isDarkMode ? "text-white" : "text-slate-900")}>
                    Recent History
                </CardTitle>
                <div className={cn("text-xs font-medium", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                    {entries.length} records
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <div className="divide-y divide-border">
                    {entries.slice(0, 5).map((entry) => (
                        <div
                            key={entry.id || entry.timestamp}
                            className={cn(
                                "flex items-center gap-4 p-4 transition-colors hover:bg-muted/50 cursor-pointer group",
                                isDarkMode ? "hover:bg-slate-800/50" : "hover:bg-slate-50"
                            )}
                        >
                            {/* Date Column */}
                            <div className="min-w-[4.5rem] text-center">
                                <div className={cn("text-sm font-bold", isDarkMode ? "text-slate-200" : "text-slate-700")}>
                                    {new Date(entry.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </div>
                                <div className={cn("text-[10px]", isDarkMode ? "text-slate-500" : "text-slate-400")}>
                                    {new Date(entry.timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                </div>
                            </div>

                            {/* Stats Columns */}
                            <div className="flex-1 grid grid-cols-3 gap-2">
                                <div className={cn("flex flex-col p-1.5 rounded-lg", isDarkMode ? "bg-emerald-950/20" : "bg-emerald-50")}>
                                    <span className={cn("text-[10px] font-bold uppercase", isDarkMode ? "text-emerald-500" : "text-emerald-700")}>Oil</span>
                                    <span className={cn("text-sm font-semibold", isDarkMode ? "text-emerald-300" : "text-emerald-900")}>
                                        {Math.round(entry.oilProduction)}
                                    </span>
                                </div>
                                <div className={cn("flex flex-col p-1.5 rounded-lg", isDarkMode ? "bg-blue-950/20" : "bg-blue-50")}>
                                    <span className={cn("text-[10px] font-bold uppercase", isDarkMode ? "text-blue-500" : "text-blue-700")}>Wtr</span>
                                    <span className={cn("text-sm font-semibold", isDarkMode ? "text-blue-300" : "text-blue-900")}>
                                        {Math.round(entry.waterProduction)}
                                    </span>
                                </div>
                                <div className={cn("flex flex-col p-1.5 rounded-lg", isDarkMode ? "bg-violet-950/20" : "bg-violet-50")}>
                                    <span className={cn("text-[10px] font-bold uppercase", isDarkMode ? "text-violet-500" : "text-violet-700")}>Gas</span>
                                    <span className={cn("text-sm font-semibold", isDarkMode ? "text-violet-300" : "text-violet-900")}>
                                        {Math.round(entry.staticPressure || 0)}
                                    </span>
                                </div>
                            </div>

                            <ChevronRight className={cn("w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity", isDarkMode ? "text-slate-600" : "text-slate-400")} />
                        </div>
                    ))}
                </div>
                {entries.length > 5 && onViewAll && (
                    <div className="p-2">
                        <Button
                            variant="ghost"
                            className="w-full text-xs h-9 text-muted-foreground hover:text-foreground"
                            onClick={onViewAll}
                        >
                            View All Entries
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
